import React, { useState, useEffect } from 'react';
import { Table, Card, DatePicker, Row, Col, Statistic, Spin } from 'antd';
import { readingService } from '../../services/api/reading.service';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { Reading } from '../../types/reading';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface ReadingHistoryProps {
  meterId: number;
}

const ReadingHistory: React.FC<ReadingHistoryProps> = ({ meterId }) => {
  const [loading, setLoading] = useState(false);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [stats, setStats] = useState({
    totalReadings: 0,
    averageConsumption: 0,
    maxReading: 0,
    minReading: 0,
    lastConsumption: 0
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    fetchReadings();
  }, [meterId, dateRange]);

  const fetchReadings = async () => {
    try {
      setLoading(true);
      const response = await readingService.getAll({
        water_meter_id: meterId,
        from_date: dateRange?.[0]?.format('YYYY-MM-DD'),
        to_date: dateRange?.[1]?.format('YYYY-MM-DD')
      });

      const sortedReadings = response.data.data.sort(
        (a, b) => dayjs(a.reading_date).valueOf() - dayjs(b.reading_date).valueOf()
      );

      setReadings(sortedReadings);
      calculateStats(sortedReadings);
    } catch (error) {
      console.error('Error fetching readings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Reading[]) => {
    if (data.length === 0) {
      setStats({
        totalReadings: 0,
        averageConsumption: 0,
        maxReading: 0,
        minReading: 0,
        lastConsumption: 0
      });
      return;
    }
    console.log(data);
    const consumptions = [];
    for (let i = 1; i < data.length; i++) {
      consumptions.push(data[i].reading_value - data[i-1].reading_value);
    }

    setStats({
      totalReadings: data.length,
      averageConsumption: consumptions.length > 0 
        ? consumptions.reduce((a, b) => a + b, 0) / consumptions.length 
        : 0,
      maxReading: Math.max(...data.map(r => r.reading_value)),
      minReading: Math.min(...data.map(r => r.reading_value)),
      lastConsumption: consumptions[consumptions.length - 1] || 0
    });
  };

  const columns = [
    {
      title: 'Datum očitavanja',
      dataIndex: 'reading_date',
      key: 'reading_date',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')
    },
    {
      title: 'Vrijednost',
      dataIndex: 'reading_value',
      key: 'reading_value',
      render: (value: number) => value.toFixed(2)
    },
    {
      title: 'Potrošnja',
      key: 'consumption',
      render: (_: any, record: Reading, index: number) => {
        if (index === 0) return '-';
        const consumption = record.reading_value - readings[index - 1].reading_value;
        return consumption.toFixed(2);
      }
    },
    {
      title: 'Period',
      key: 'period',
      render: (_: any, record: Reading, index: number) => {
        if (index === 0) return '-';
        const days = dayjs(record.reading_date).diff(dayjs(readings[index - 1].reading_date), 'day');
        return `${days} dana`;
      }
    },
    {
      title: 'Dnevni prosjek',
      key: 'daily_average',
      render: (_: any, record: Reading, index: number) => {
        if (index === 0) return '-';
        const consumption = record.reading_value - readings[index - 1].reading_value;
        const days = dayjs(record.reading_date).diff(dayjs(readings[index - 1].reading_date), 'day');
        const daily = days > 0 ? consumption / days : 0;
        return daily.toFixed(2);
      }
    }
  ];

  const chartData = readings.map((reading, index) => ({
    date: dayjs(reading.reading_date).format('DD.MM.YYYY'),
    value: reading.reading_value,
    consumption: index === 0 ? 0 : reading.reading_value - readings[index - 1].reading_value
  }));

  return (
    <div>
      <Row gutter={[16, 16]} className="mb-4">
        <Col span={24}>
          <RangePicker 
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Ukupno očitavanja"
              value={stats.totalReadings}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Prosječna potrošnja"
              value={stats.averageConsumption}
              precision={2}
              suffix="m³"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Zadnja potrošnja"
              value={stats.lastConsumption}
              precision={2}
              suffix="m³"
            />
          </Card>
        </Col>
      </Row>

      <Card title="Grafički prikaz" className="mb-4">
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="value"
                stroke="#0284c7"
                name="Stanje (m³)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="consumption"
                stroke="#10b981"
                name="Potrošnja (m³)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={readings}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Ukupno ${total} očitavanja`
        }}
      />
    </div>
  );
};

export default ReadingHistory;